import { ThemeProvider, useTheme } from '@mui/material';
import { DatePicker, type DatePickerProps, LocalizationProvider, type PickerValidDate } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { esES, ptBR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import { useSelector } from 'react-redux';
import { mergeOutlinedFilterFieldSx } from '/@/mui/outlined-filter-field-sx';
import { getSessionLanguage } from '/@/store/session/selectors';

const MuiDatePicker = <TDate extends PickerValidDate, TEnableAccessibleFieldDOMStructure extends boolean = false>(
    props: DatePickerProps<TDate, TEnableAccessibleFieldDOMStructure> &
        React.RefAttributes<HTMLDivElement> & { size?: 'small' | 'medium'; fullWidth?: boolean }
) => {
    const materialTheme = useTheme();
    const language = useSelector(getSessionLanguage);

    const { slotProps, size, fullWidth, ...restProps } = props;

    const hasFloatingLabel = Boolean(slotProps?.textField?.label ?? props.label);

    const getLocale = () => {
        switch (language) {
            case 'es':
                return esES;
            case 'pt':
                return ptBR;
            default:
                return undefined;
        }
    };

    const handleChange = (value: PickerValidDate | null | undefined) => {
        if (props.onChange) {
            props.onChange(value ?? null);
        }
    };

    return (
        <ThemeProvider theme={materialTheme}>
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={language}
                localeText={getLocale()?.components?.MuiLocalizationProvider?.defaultProps?.localeText || {}}
            >
                <DatePicker
                    {...restProps}
                    value={props.value || null}
                    onChange={handleChange}
                    slotProps={{
                        ...slotProps,
                        textField: {
                            size,
                            fullWidth,
                            ...slotProps?.textField,
                            sx: mergeOutlinedFilterFieldSx(slotProps?.textField?.sx, {
                                hasFloatingLabel,
                            }),
                        },
                    }}
                />
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default MuiDatePicker;
